import apiConnector from "api-connector";
import axios from "axios";

export type RoleData = {
  name: string;
  national_identity_number: string;
  uuid: string;
  email: string;
};

export type PolicyRolesResponse = {
  life_assured: RoleData[];
  policy_holder: RoleData[];
  beneficial: RoleData[];
  asset_manager: RoleData[];
  view_only_users: RoleData[];
};

export type ProfileRolesResponse = {
  roles: RoleData[];
};

export type UserProfileRolePayload = {
  userUuid: string;
  roleName: string;
};

export type PolicyProfileRolePayload = {
  profileUuid: string;
  roleName: string;
};

export const getEnvBaseUrl = (): string => {
  // Match helpers/config default fallback
  return (import.meta as any)?.env?.VITE_MIDDLEMAN_URL || "http://127.0.0.1:8000";
};

// Create a middleman instance bound to a specific baseUrl
export const createMiddleman = (baseUrl: string) => {
  const apiUrl = `${baseUrl}/api`;
  const csrfUrl = `${baseUrl}/sanctum/csrf-cookie`;

  const base = apiConnector(apiUrl);

  const mm = {
    ...base,
    // Domain helpers (kept for backwards compatibility)
    getProfileRoles: async (profileUuid: string): Promise<ProfileRolesResponse> => {
      const res = await base.get(`/userprofileroles/${profileUuid}`);
      return res.data as ProfileRolesResponse;
    },
    addUserProfileRole: async (profileUuid: string, userUuid: string, roleName: string) => {
      const payload: UserProfileRolePayload = { userUuid, roleName };
      return await base.post(`/userprofileroles/${profileUuid}`, payload);
    },
    removeUserProfileRole: async (profileUuid: string, userUuid: string, roleName: string) => {
      const payload: UserProfileRolePayload = { userUuid, roleName };
      return await base.promisePost(`/userprofileroles/delete/${profileUuid}`, payload);
    },
    getPolicyRoles: async (portfolioUuid: string): Promise<PolicyRolesResponse> => {
      const response = await base.get(`/policyroles/${portfolioUuid}`);
      return response.data as PolicyRolesResponse;
    },
    addPolicyProfileRole: async (portfolioUuid: string, profileUuid: string, roleName: string) => {
      const payload: PolicyProfileRolePayload = { profileUuid, roleName };
      return await base.post(`/policyroles/${portfolioUuid}`, payload);
    },
    removePolicyProfileRole: async (portfolioUuid: string, profileUuid: string, roleName: string) => {
      const payload: PolicyProfileRolePayload = { profileUuid, roleName };
      return await base.promisePost(`/policyroles/delete/${portfolioUuid}`, payload);
    },

    // Backoffice roles helpers
    getBrokers: async () => {
      const response = await base.get(`/crm/brokers`);
      return response.data;
    },
    getUserRoles: async () => {
      const response = await base.get(`/roles?type=user`);
      return response.data;
    },
    getRolesForUser: async (userUuid: string) => {
      const response = await base.get(`/ql/crm/users/${userUuid}/roles`);
      return response.data;
    },
    getUser: async (userUuid: string) => {
      const response = await base.get(`/users/${userUuid}`);
      return response.data;
    },
    addUserRole: async (userUuid: string, roleName: string) => {
      const payload: UserProfileRolePayload = { userUuid, roleName };
      return await base.post(`/ql/crm/users/${userUuid}/role`, payload);
    },
    removeUserRole: async (userUuid: string, roleName: string) => {
      const payload: UserProfileRolePayload = { userUuid, roleName };
      return await base.post(`/ql/crm/users/${userUuid}/role/remove`, payload);
    },
    getUserRoleHistory: async (userUuid: string) => {
      const response = await base.get(`/ql/crm/users/${userUuid}/role-history`);
      return response.data;
    },
  } as any;

  // Attach csrf helper bound to this baseUrl
  mm.csrf = () => axios.get(csrfUrl, { withCredentials: true });

  return mm;
};


